// src/pages/Films.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Camera, 
  Film, 
  Mic, 
  Edit,
  Mail,
  Phone,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
// Header is now in PublicLayout

// Import all film images for masonry gallery
import film1 from '../assets/images/Films/IMG-20250613-WA0006.jpg';
import film2 from '../assets/images/Films/IMG-20250613-WA0007.jpg';
import film3 from '../assets/images/Films/IMG-20250613-WA0008.jpg';
import film4 from '../assets/images/Films/IMG-20250613-WA0009.jpg';
import film5 from '../assets/images/Films/IMG-20250613-WA0010.jpg';
import film6 from '../assets/images/Films/IMG-20250613-WA0011.jpg';
import film7 from '../assets/images/Films/IMG-20250613-WA0012.jpg';
import film8 from '../assets/images/Films/IMG-20250613-WA0013.jpg';
import film9 from '../assets/images/Films/IMG-20250613-WA0014.jpg';
import film10 from '../assets/images/Films/IMG-20250613-WA0015.jpg';
import film11 from '../assets/images/Films/IMG-20250613-WA0016.jpg';
import film12 from '../assets/images/Films/IMG-20250613-WA0017.jpg';
import film13 from '../assets/images/Films/IMG-20250613-WA0018.jpg';
import film14 from '../assets/images/Films/IMG-20250613-WA0019.jpg';
import film15 from '../assets/images/Films/IMG-20250613-WA0020.jpg';
import film16 from '../assets/images/Films/IMG-20250613-WA0021.jpg';
import film17 from '../assets/images/Films/IMG-20250613-WA0022.jpg';
import film18 from '../assets/images/Films/IMG-20250613-WA0023.jpg';
import film19 from '../assets/images/Films/IMG-20250613-WA0024.jpg';
import film20 from '../assets/images/Films/IMG-20250613-WA0025.jpg';
import film21 from '../assets/images/Films/IMG-20250613-WA0026.jpg';
import film22 from '../assets/images/Films/IMG-20250613-WA0027.jpg';
import film23 from '../assets/images/Films/IMG-20250613-WA0028.jpg';
import film24 from '../assets/images/Films/IMG-20250613-WA0029.jpg';
import film25 from '../assets/images/Films/IMG-20250613-WA0030.jpg';
import film26 from '../assets/images/Films/IMG-20250613-WA0031.jpg';
import film27 from '../assets/images/Films/IMG-20250613-WA0032.jpg';
import film28 from '../assets/images/Films/IMG-20250613-WA0033.jpg';

// Video imports
import showreel1 from '../assets/images/Films/VID-20250613-WA0013.mp4';
import showreel2 from '../assets/images/Films/VID-20250613-WA0014.mp4';

const Films = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const videoRef = useRef(null);

  // All film images with varied heights for masonry effect
  const filmImages = [
    { src: film1, height: 'tall' },
    { src: film2, height: 'medium' },
    { src: film3, height: 'medium' },
    { src: film4, height: 'tall' },
    { src: film5, height: 'short' },
    { src: film6, height: 'medium' },
    { src: film7, height: 'tall' },
    { src: film8, height: 'short' },
    { src: film9, height: 'medium' },
    { src: film10, height: 'tall' },
    { src: film11, height: 'medium' },
    { src: film12, height: 'short' },
    { src: film13, height: 'tall' },
    { src: film14, height: 'medium' },
    { src: film15, height: 'medium' },
    { src: film16, height: 'tall' },
    { src: film17, height: 'short' },
    { src: film18, height: 'medium' },
    { src: film19, height: 'tall' },
    { src: film20, height: 'medium' },
    { src: film21, height: 'short' },
    { src: film22, height: 'tall' },
    { src: film23, height: 'medium' },
    { src: film24, height: 'medium' },
    { src: film25, height: 'tall' },
    { src: film26, height: 'short' },
    { src: film27, height: 'medium' },
    { src: film28, height: 'tall' },
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isVideoMuted;
      setIsVideoMuted(!isVideoMuted);
    }
  };

  const services = [
    {
      id: 'documentaries',
      title: 'Documentaries',
      icon: <Film className="w-6 h-6" />,
      description: 'Compelling real-world stories that educate, inspire and move audiences.',
      features: ['Research & Development', 'Professional Filming', 'Post-Production', 'Distribution Strategy'],
    },
    {
      id: 'podcasts',
      title: 'Podcasts',
      icon: <Mic className="w-6 h-6" />,
      description: 'High-quality audio content that engages and builds loyal audiences.',
      features: ['Studio Recording', 'Audio Engineering', 'Content Strategy', 'Platform Distribution'],
    },
    {
      id: 'storytelling',
      title: 'Storytelling',
      icon: <Camera className="w-6 h-6" />,
      description: 'Narrative-driven content that connects brands with their audiences.',
      features: ['Brand Narratives', 'Visual Storytelling', 'Script Development', 'Creative Direction'],
    },
    {
      id: 'content',
      title: 'Content Creation',
      icon: <Edit className="w-6 h-6" />,
      description: 'Full-spectrum content production from concept to final delivery.',
      features: ['Video Production', 'Photography', 'Social Media Content', 'Marketing Materials'],
    }
  ];

  // Create masonry columns
  const createColumns = () => {
    const columns = [[], [], [], []];
    filmImages.forEach((item, index) => {
      columns[index % 4].push(item);
    });
    return columns;
  };

  const columns = createColumns();

  const getHeightClass = (height) => {
    switch (height) {
      case 'tall': return 'h-[500px]';
      case 'medium': return 'h-[350px]';
      case 'short': return 'h-[250px]';
      default: return 'h-[350px]';
    }
  };

  return (
    <div className="relative min-h-screen bg-pontso-black text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${film14})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-pontso-black via-pontso-black/95 to-pontso-black" />
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="mb-8"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 className={`font-heading text-white mb-4 tracking-tight ${isMobile ? 'text-5xl' : 'text-7xl lg:text-8xl'}`}>
                THE CANOA
              </h1>
              <div className="w-24 h-px bg-pontso-gold mx-auto mb-4" />
              <p className={`text-pontso-gold font-body font-light tracking-wider ${isMobile ? 'text-base' : 'text-xl'}`}>
                FILMS
              </p>
            </motion.div>
            
            <motion.p 
              className={`text-white/90 font-body font-light mb-12 leading-relaxed ${isMobile ? 'text-lg px-4' : 'text-2xl lg:text-3xl max-w-4xl mx-auto'}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Crafting visual narratives that transcend boundaries and inspire change
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative z-10 py-32 bg-pontso-black-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className={`font-heading text-white mb-6 ${isMobile ? 'text-4xl' : 'text-5xl lg:text-6xl'}`}>
              Our Creative Services
            </h2>
            <div className="w-24 h-px bg-pontso-gold mx-auto mb-6" />
            <p className={`text-white/70 font-body font-light max-w-3xl mx-auto ${isMobile ? 'text-base px-4' : 'text-lg'}`}>
              From concept to completion, we deliver exceptional visual content that resonates with audiences and drives meaningful engagement.
            </p>
          </motion.div>

          <div className={`grid gap-8 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group relative p-8 bg-pontso-charcoal border border-pontso-gold/20 rounded-2xl hover:border-pontso-gold transition-all duration-300"
              >
                <div className="w-16 h-16 bg-pontso-gold/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-pontso-gold/30 transition-colors border border-pontso-gold/30">
                  <div className="text-pontso-gold">
                    {service.icon}
                  </div>
                </div>

                <h3 className="text-xl font-heading font-normal text-white mb-4">{service.title}</h3>
                <p className="text-white/70 font-body font-light mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-white/60 text-sm font-body font-light">
                      <div className="w-1.5 h-1.5 bg-pontso-gold rounded-full mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Masonry Gallery Section */}
      <section className="relative z-10 py-32 bg-pontso-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className={`font-heading text-white mb-6 ${isMobile ? 'text-4xl' : 'text-5xl lg:text-6xl'}`}>
              Our Portfolio
            </h2>
            <div className="w-24 h-px bg-pontso-gold mx-auto mb-6" />
            <p className={`text-white/70 font-body font-light max-w-3xl mx-auto ${isMobile ? 'text-base px-4' : 'text-lg'}`}>
              A glimpse into our creative process and the exceptional work we deliver.
            </p>
          </motion.div>

          {/* Masonry Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {columns.map((column, colIndex) => (
              <div key={colIndex} className="flex flex-col gap-4">
                {column.map((item, itemIndex) => (
                  <motion.div
                    key={`${colIndex}-${itemIndex}`}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: (colIndex * 0.1) + (itemIndex * 0.05) }}
                    viewport={{ once: true }}
                    className="group relative overflow-hidden rounded-xl cursor-pointer"
                    onClick={() => setSelectedImage(item.src)}
                  >
                    <div className={`${getHeightClass(item.height)} relative overflow-hidden`}>
                      <img
                        src={item.src}
                        alt={`Film production ${colIndex * column.length + itemIndex + 1}`}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-pontso-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </motion.div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Showreel Section */}
      <section className="relative z-10 py-32 bg-pontso-black-soft">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className={`font-heading text-white mb-6 ${isMobile ? 'text-4xl' : 'text-5xl lg:text-6xl'}`}>
              Our Showreel
            </h2>
            <div className="w-24 h-px bg-pontso-gold mx-auto mb-6" />
            <p className={`text-white/70 font-body font-light ${isMobile ? 'text-base px-4' : 'text-lg'}`}>
              Experience the quality and creativity that defines our work.
            </p>
          </motion.div>

          <motion.div 
            className="relative rounded-2xl overflow-hidden shadow-2xl border border-pontso-gold/20"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <video
              ref={videoRef}
              autoPlay
              loop
              muted={isVideoMuted}
              playsInline
              className="w-full h-auto"
              poster={film14}
            >
              <source src={showreel2} type="video/mp4" />
            </video>

            <div className="absolute top-4 right-4 flex gap-2 z-20">
              <motion.button
                onClick={togglePlayPause}
                className="bg-pontso-black/70 backdrop-blur-sm text-white rounded-xl p-3 hover:bg-pontso-black/90 transition-colors border border-pontso-gold/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isVideoPlaying ? <Pause size={16} /> : <Play size={16} />}
              </motion.button>
              
              <motion.button
                onClick={toggleMute}
                className="bg-pontso-black/70 backdrop-blur-sm text-white rounded-xl p-3 hover:bg-pontso-black/90 transition-colors border border-pontso-gold/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isVideoMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative z-10 py-32 bg-pontso-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className={`font-heading text-white mb-6 ${isMobile ? 'text-4xl' : 'text-5xl lg:text-6xl'}`}>
              Let's Create Together
            </h2>
            <div className="w-24 h-px bg-pontso-gold mx-auto mb-6" />
            <p className={`text-white/70 font-body font-light mb-12 ${isMobile ? 'text-base px-4' : 'text-lg max-w-2xl mx-auto'}`}>
              Ready to bring your vision to life? Let's discuss how we can create compelling content that resonates with your audience.
            </p>

            <div className={`flex flex-col sm:flex-row gap-6 justify-center ${isMobile ? 'px-4' : ''}`}>
              <a
                href="mailto:info@pontsoyarona.co.za"
                className="inline-flex items-center justify-center px-8 py-4 bg-pontso-gold text-pontso-black rounded-lg hover:bg-pontso-gold-light transition-all duration-300 font-medium group"
              >
                <Mail className="w-5 h-5 mr-2" />
                <span>Get in Touch</span>
                <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
              
              <a
                href="tel:+27678201034"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-pontso-gold text-pontso-gold rounded-lg hover:bg-pontso-gold/10 transition-all duration-300 font-body font-light"
              >
                <Phone className="w-5 h-5 mr-2" />
                <span>Call Now</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-pontso-black/95 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={selectedImage}
              alt="Selected film production"
              className="max-w-full max-h-full object-contain rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-3 bg-pontso-black/70 backdrop-blur-md text-white rounded-xl hover:bg-pontso-black/90 transition-colors border border-pontso-gold/30"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Films;
